<!--
	Read-only, descriptor-driven legend of the layers currently switched on, with
	their symbology (no checkboxes). A `partOf` sublayer gets no row of its own;
	its symbology folds into the primary layer's row unless it sets `hideSymbology`.
-->
<script lang="ts">
	import LegendSection from '#lib/components/LegendSection.svelte';
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import { activeLegendLayers } from './activeLegendLayers';
	import { appStore } from '#lib/stores/appStore.svelte.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { mapInstanceStore } from '#lib/stores/mapInstanceStore.svelte.js';
	import { mapExtentStore } from '#lib/stores/mapExtentStore.svelte.js';
	import type { ILayerSource } from '#lib/types.js';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import { patchStyle, type SymbologyItem } from './symbology';
	import SymbologySwatch from './SymbologySwatch.svelte';
	import type { LegendSection as LegendSectionConfig } from './types';
	import { computeRenderedSections } from './activeLegendModel';

	const {
		sections = appStore.app?.legendSections ?? [],
		spritePath = appStore.app?.legendSpritePath
	}: { sections?: LegendSectionConfig[]; spritePath?: string } = $props();

	// Wide view of the envelope, so descriptor keys can index it.
	const systemData = $derived(
		geodataStore.systemData as Record<string, Record<string, ILayerSource | null>> | null
	);

	// Rebuilds on every map move / zoom / tile load: resolve which descriptor rows
	// are switched on, then derive each row's symbology from the classes actually
	// drawn in the current viewport (`queryRenderedFeatures`).
	const renderedSections = $derived.by(() => {
		void mapExtentStore.viewBounds;
		void mapExtentStore.renderTick;
		return computeRenderedSections(
			sections,
			systemData,
			activeLegendLayers(),
			mapInstanceStore.map
		);
	});
</script>

{#snippet symbologyList(items: SymbologyItem[])}
	<ul class="symbologyList">
		{#each items as item, i (i)}
			<li>
				<SymbologySwatch swatch={item.swatch} {spritePath} />
				<span>{item.label}</span>
			</li>
		{/each}
	</ul>
{/snippet}

<div class="legendBox">
	{#if !renderedSections.length}
		<p class="empty">
			No layers are switched on. Enable layers in the Layers tab to see them here.
		</p>
	{:else}
		{#each renderedSections as section (section.label)}
			<LegendSection heading={section.label}>
				{#if section.hints.length}
					<div class="surfaceHint">
						{#each section.hints as hint (hint.label)}
							<span class="hintItem">
								<SymbologySwatch swatch={hint.swatch} {spritePath} hint />
								<span>{hint.label}</span>
							</span>
						{/each}
					</div>
				{/if}
				{#each section.listRows as row (row.layerId)}
					<div class="legendRowBox">
						{#if row.symbology.length}
							<div class="rowLabel">{row.label}</div>
							{@render symbologyList(row.symbology)}
						{:else}
							<div class="rowLine">
								<LegendPatch
									size="sm"
									color={row.legend?.legendItems[0]?.color ?? PATCH_FALLBACK}
									shape={patchStyle(row.source?.layer.type)} />
								<span class="rowLabel">{row.label}</span>
							</div>
						{/if}
					</div>
				{/each}
			</LegendSection>
		{/each}
	{/if}
</div>

<style>
	.legendBox {
		width: calc(var(--legend-width) + 9rem);
	}

	.empty {
		font-size: var(--font-size-sm);
	}

	/* One-line paved/unpaved hint under the section heading. */
	.surfaceHint {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-1) var(--space-3);
		margin-bottom: var(--space-3);
		color: var(--text-muted);
		font-size: var(--font-size-xs);

		.hintItem {
			display: flex;
			align-items: center;
			gap: var(--space-1);
		}
	}

	.rowLabel {
		font-weight: var(--weight-semibold);
	}

	.rowLine {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding-left: var(--space-2);
	}

	.symbologyList {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		margin: var(--space-1) 0 var(--space-2);
		padding: 0 0 0 var(--space-2);
		list-style: none;

		li {
			display: flex;
			align-items: center;
			gap: var(--space-2);
		}
	}
</style>
